export const attachUserIdentityIdToAWSIoTPolicy = async (userIdentityId) => {
    return `aws iot attach-policy --policy-name dashboardAmplifyIoTPolicy --target ${userIdentityId}`;
}

/** Leaderboard XP is 75% of game points. */
export const GAME_POINTS_TO_XP_RATIO = 0.75;

/** RapFire APIs use `score`; Housie uses `points`. */
export const getPlayerPoints = (player) =>
    Number(
        player?.points ??
        player?.score ??
        player?.gamePoints ??
        player?.totalScore ??
        player?.cumulativeScore ??
        player?.totalPoints ??
        0
    ) || 0;

export const gamePointsToXp = (points) =>
    Math.round((Number(points) || 0) * GAME_POINTS_TO_XP_RATIO);

/** Prefer API `XPs` when present; otherwise 75% of game points. */
export const getPlayerXp = (player) => {
    const raw =
        player?.XPs ??
        player?.xps ??
        player?.XP ??
        player?.xp;
    if (raw != null && raw !== '') {
        const value = Number(raw);
        if (Number.isFinite(value)) return value;
    }
    return gamePointsToXp(getPlayerPoints(player));
};

/** Per-round scores from tournament `roundScores` / `roundScore`. */
export const extractRoundScores = (player = {}) => {
    const raw = player.roundScores || player.roundScore;
    if (!Array.isArray(raw) || !raw.length) return [];
    return raw
        .map((item, index) => ({
            roundNumber: Number(item?.roundNumber ?? index + 1) || index + 1,
            points: Number(item?.points ?? item?.score ?? 0) || 0,
            gameTypeIcon: item?.gameTypeIcon || item?.icon || '',
            gameType: item?.gameType || '',
        }))
        .sort((a, b) => a.roundNumber - b.roundNumber);
};

export const normalizeLeaderboardPlayer = (player = {}, index = 0) => {
    const points = getPlayerPoints(player);
    return {
        ...player,
        username: player.username || player.userName || player.displayName || 'Player',
        avatar: player.avatar || player.profilePic || player.userImage || '',
        points,
        xp: getPlayerXp(player),
        rank: player.rank ?? index + 1,
    };
};

export const extractLeaderboardPlayers = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.participants)) return data.participants;
    if (Array.isArray(data?.users)) return data.users;
    if (Array.isArray(data?.leaderboard)) return data.leaderboard;
    if (Array.isArray(data?.standings)) return data.standings;
    if (Array.isArray(data?.players)) return data.players;
    if (Array.isArray(data?.rankings)) return data.rankings;
    if (Array.isArray(data?.userState)) return data.userState;
    if (Array.isArray(data?.usersState)) return data.usersState;
    if (Array.isArray(data?.contest?.participants)) return data.contest.participants;
    if (Array.isArray(data?.result?.users)) return data.result.users;
    if (Array.isArray(data?.result?.leaderboard)) return data.result.leaderboard;
    if (Array.isArray(data?.tournament?.leaderboard)) return data.tournament.leaderboard;
    return [];
};

const TOURNAMENT_GAME_TYPE_ALIASES = {
    rap_fire: 'rap_fire',
    rapfire: 'rap_fire',
    fire: 'rap_fire',
    rf: 'rap_fire',
    quiz: 'rap_fire',
    rap_housie: 'rap_housie',
    raphousie: 'rap_housie',
    housie: 'rap_housie',
    rh: 'rap_housie',
    tambola: 'rap_housie',
};

const TOURNAMENT_STATUS_ALIASES = {
    completed: 'completed',
    complete: 'completed',
    done: 'completed',
    finished: 'completed',
    next: 'next',
    up_next: 'up_next',
    upcoming: 'up_next',
    live: 'live',
    current: 'live',
    in_progress: 'live',
    active: 'live',
};

const slugifyTournamentKey = (value) =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_');

export const normalizeTournamentStage = (stage = {}, index = 0) => {
    const rawType = slugifyTournamentKey(
        stage.gameType || stage.game || stage.type || stage.contestType || stage.name || ''
    );
    const gameType =
        TOURNAMENT_GAME_TYPE_ALIASES[rawType] ||
        (rawType.includes('housie') || rawType.includes('tambola')
            ? 'rap_housie'
            : 'rap_fire');
    const rawStatus = slugifyTournamentKey(stage.status || stage.state || '');
    const status = TOURNAMENT_STATUS_ALIASES[rawStatus] || (index === 0 ? 'completed' : 'up_next');
    const name =
        stage.name ||
        stage.title ||
        stage.gameName ||
        (gameType === 'rap_housie' ? 'RapHousie' : 'RapFire');

    return {
        ...stage,
        id: stage.id || stage.contestId || stage.gameId || stage._id || `stage-${index + 1}`,
        gameType,
        name,
        status,
        icon: stage.gameTypeIcon || stage.icon || '',
        questionNumber:
            Number(
                stage.questionNumber ??
                stage.questionIndex ??
                stage.questionNo ??
                0
            ) || 0,
        roundNumber: Number(stage.roundNumber ?? index + 1) || index + 1,
    };
};

export const extractTournamentStages = (data) => {
    const stages =
        (Array.isArray(data?.progress) && data.progress) ||
        (Array.isArray(data?.stages) && data.stages) ||
        (Array.isArray(data?.games) && data.games) ||
        (Array.isArray(data?.rounds) && data.rounds) ||
        (Array.isArray(data?.schedule) && data.schedule) ||
        (Array.isArray(data?.gameSequence) && data.gameSequence) ||
        (Array.isArray(data?.tournament?.stages) && data.tournament.stages) ||
        (Array.isArray(data?.tournament?.games) && data.tournament.games) ||
        (Array.isArray(data?.result?.stages) && data.result.stages) ||
        [];
    let rapFireQuestion = 0;
    return stages.map((stage, index) => {
        const normalized = normalizeTournamentStage(stage, index);
        if (normalized.gameType !== 'rap_fire') {
            return { ...normalized, questionNumber: 0 };
        }
        if (normalized.questionNumber > 0) return normalized;
        rapFireQuestion += 1;
        return {
            ...normalized,
            questionNumber: normalized.roundNumber || rapFireQuestion,
        };
    });
};

const firstNonEmpty = (...values) => {
    const match = values.find((value) => String(value || '').trim());
    return match ? String(match).trim() : '';
};

/**
 * Prize allotment rank range.
 * Canonical API: `{ rankFrom, rankTo, prizeTitle, prizeIcon }`.
 * Older payloads used `{ rank }`.
 */
const getAllotmentRange = (item = {}) => {
    const from = Number(item.rankFrom ?? item.rank);
    if (!Number.isFinite(from) || from <= 0) return null;
    const toRaw = Number(item.rankTo ?? item.rankFrom ?? item.rank);
    const to = Number.isFinite(toRaw) && toRaw >= from ? toRaw : from;
    return { rankFrom: from, rankTo: to, rank: from };
};

const collectPrizeAllotmentSource = (data = {}) => {
    if (Array.isArray(data?.tournamentRewardAllotmentArray)
        && data.tournamentRewardAllotmentArray.length) {
        return data.tournamentRewardAllotmentArray;
    }
    if (Array.isArray(data?.prizeTabs) && data.prizeTabs.length) {
        return data.prizeTabs;
    }
    return (Array.isArray(data?.winners) ? data.winners : []).filter(
        (winner) => winner?.prizeTitle || winner?.prizeIcon
    );
};

export const mergeTournamentPrizes = (players = [], data = {}) => {
    const byRank = {};
    const byUser = {};

    (Array.isArray(data?.winners) ? data.winners : []).forEach((winner) => {
        if (winner?.userId) byUser[winner.userId] = winner;
        if (winner?.rank != null) byRank[Number(winner.rank)] = winner;
    });

    collectPrizeAllotmentSource(data).forEach((allotment) => {
        const range = getAllotmentRange(allotment);
        if (!range) return;
        for (let rank = range.rankFrom; rank <= range.rankTo; rank += 1) {
            byRank[rank] = {
                ...byRank[rank],
                ...allotment,
                rank,
            };
        }
    });

    return players.map((player) => {
        const fromUser = byUser[player.userId] || {};
        const fromRank = byRank[player.rank] || {};
        return {
            ...player,
            prizeIcon: firstNonEmpty(
                player.prizeIcon,
                fromUser.prizeIcon,
                fromRank.prizeIcon
            ),
            prizeTitle: firstNonEmpty(
                player.prizeTitle,
                fromUser.prizeTitle,
                fromRank.prizeTitle
            ),
        };
    });
};

export const TOURNAMENT_PRIZE_SLOT_COUNT = 10;

export const hasTournamentPrizePlayerDetails = (item = {}) =>
    Boolean(
        firstNonEmpty(
            item.userName,
            item.username,
            item.displayName,
            item.profilePic,
            item.avatar,
            item.userImage,
            item.userId,
            item.id,
            item._id
        )
    );

/**
 * Prize list from `tournamentRewardAllotmentArray` (rankFrom/rankTo).
 * User name/photo from `winners` / leaderboard players at that rank.
 * Ranks without player details are omitted (no empty winner card or prize).
 * Missing prize ranks reuse the last allotment prize when a player exists.
 */
export const expandTournamentPrizeAllotments = (
    allotments = [],
    winners = [],
    players = []
) => {
    const byRank = {};
    (Array.isArray(allotments) ? allotments : []).forEach((item) => {
        const range = getAllotmentRange(item);
        if (!range) return;
        for (let rank = range.rankFrom; rank <= range.rankTo; rank += 1) {
            byRank[rank] = { ...item, rank };
        }
    });

    const orderedRanks = Object.keys(byRank)
        .map(Number)
        .sort((a, b) => a - b);
    if (!orderedRanks.length) return [];

    const personByRank = {};
    (Array.isArray(players) ? players : []).forEach((player) => {
        const rank = Number(player?.rank);
        if (rank > 0) personByRank[rank] = { ...personByRank[rank], ...player };
    });
    (Array.isArray(winners) ? winners : []).forEach((winner) => {
        const rank = Number(winner?.rank);
        if (rank > 0) personByRank[rank] = { ...personByRank[rank], ...winner };
    });

    const firstAllotment = byRank[orderedRanks[0]];
    let lastAllotment = firstAllotment;
    const slides = [];
    for (let rank = 1; rank <= TOURNAMENT_PRIZE_SLOT_COUNT; rank += 1) {
        if (byRank[rank]) lastAllotment = byRank[rank];
        const allotment = byRank[rank] || lastAllotment || {};
        const person = personByRank[rank] || {};
        if (!hasTournamentPrizePlayerDetails(person)) continue;
        slides.push({
            rank,
            rankFrom: rank,
            rankTo: rank,
            userId: person.userId || person.id || person._id || '',
            prizeTitle: firstNonEmpty(allotment.prizeTitle, person.prizeTitle),
            prizeIcon: firstNonEmpty(allotment.prizeIcon, person.prizeIcon),
            username:
                person.userName ||
                person.username ||
                person.displayName ||
                '',
            avatar:
                person.profilePic ||
                person.avatar ||
                person.userImage ||
                '',
        });
    }
    return slides;
};

export const extractTournamentPrizeAllotments = (data = {}, players = []) => {
    const winners = Array.isArray(data?.winners) ? data.winners : [];
    const allotments = Array.isArray(data?.tournamentRewardAllotmentArray)
        ? data.tournamentRewardAllotmentArray
        : collectPrizeAllotmentSource(data);
    return expandTournamentPrizeAllotments(allotments, winners, players);
};